import { api } from '../../api/fetch';

export type ReferenceImageDisposition = 'include' | 'available' | 'exclude';

export interface ReferenceImage {
  id: number;
  test_specification_id: number;
  disposition: ReferenceImageDisposition;
  archived: boolean;
  url: string | null;
  thumbnail_url: string | null;
  preview_url: string | null;
  name: string | null;
  /** Optional inspector-set title shown wherever the image is surfaced. */
  display_name: string | null;
  created_at: string;
}

/**
 * A Reference Image as it appears resolved onto a Test Item (vs. authored on the
 * spec). Differentiated from an item's own captured images by `reference: true`,
 * and carrying the per-item `included` election plus its `source` (auto default
 * vs. explicit inspector choice — see ADR-0003).
 */
export interface ResolvedReferenceImage {
  reference: true;
  id: number;
  test_specification_id: number;
  disposition: ReferenceImageDisposition;
  included: boolean;
  source: 'auto' | 'explicit';
  archived: boolean;
  url: string | null;
  thumbnail_url: string | null;
  preview_url: string | null;
  name: string | null;
  display_name: string | null;
}

/**
 * Default resolution for a Test Item that has no server-resolved reference
 * images yet (e.g. a brand-new, unsaved item — there is no election to
 * override against). Mirrors the server's default-election rule: `exclude`
 * never resolves; `include` defaults on; `available` defaults off but is
 * still resolved so the inspector can elect it (ADR-0003).
 */
export const deriveDefaultResolvedReferenceImages = (
  specImages: ReferenceImage[],
): ResolvedReferenceImage[] =>
  specImages
    .filter((img) => img.disposition !== 'exclude')
    .map((img) => ({
      reference: true as const,
      id: img.id,
      test_specification_id: img.test_specification_id,
      disposition: img.disposition,
      included: img.disposition === 'include',
      source: 'auto' as const,
      archived: img.archived,
      url: img.url,
      thumbnail_url: img.thumbnail_url,
      preview_url: img.preview_url,
      name: img.name,
      display_name: img.display_name,
    }));

const baseUrl = (testSuiteId: number, testSpecificationId: number) =>
  `/api/v1/test-suites/${testSuiteId}/tests/${testSpecificationId}/reference-images`;

export const fetchReferenceImages = async (
  testSuiteId: number,
  testSpecificationId: number,
) => api.get(baseUrl(testSuiteId, testSpecificationId));

export const uploadReferenceImage = async (
  testSuiteId: number,
  testSpecificationId: number,
  file: File,
  disposition?: ReferenceImageDisposition,
  displayName?: string,
) => {
  const form = new FormData();
  form.append('file', file);
  if (disposition) {
    form.append('disposition', disposition);
  }
  if (displayName) {
    form.append('display_name', displayName);
  }
  return api.post(baseUrl(testSuiteId, testSpecificationId), form);
};

export const updateReferenceImageDisposition = async (
  testSuiteId: number,
  testSpecificationId: number,
  referenceImageId: number,
  disposition: ReferenceImageDisposition,
) =>
  api.put(`${baseUrl(testSuiteId, testSpecificationId)}/${referenceImageId}`, {
    disposition,
  });

/**
 * Update the optional display title for a Reference Image. The API's update
 * endpoint requires `disposition` on every call, so the current value must be
 * passed through unchanged alongside the new name.
 */
export const updateReferenceImageDisplayName = async (
  testSuiteId: number,
  testSpecificationId: number,
  referenceImageId: number,
  disposition: ReferenceImageDisposition,
  displayName: string,
) =>
  api.put(`${baseUrl(testSuiteId, testSpecificationId)}/${referenceImageId}`, {
    disposition,
    display_name: displayName,
  });

export const archiveReferenceImage = async (
  testSuiteId: number,
  testSpecificationId: number,
  referenceImageId: number,
) => api.delete(`${baseUrl(testSuiteId, testSpecificationId)}/${referenceImageId}`);

/**
 * Elect (or de-elect) a Reference Image for a single Test Item. Recorded as an
 * explicit per-item choice on the API; excluded images are rejected server-side.
 */
export const electTestItemReferenceImage = async (
  testItemId: number,
  referenceImageId: number,
  included: boolean,
) =>
  api.put(`/api/v1/test-items/${testItemId}/reference-images/${referenceImageId}`, {
    included,
  });
